import { CustomUser } from 'api';
import { signInPath } from 'common/config/urls';
import {
  AUTH_OTP_REMEMBER_COOKIE_PREFIX,
  AUTH_OTP_REMEMBER_LEGACY_COOKIE_PREFIX,
  OTP_REMEMBER_DEVICE_DAYS,
  SIGN_IN_PRODUCT,
  SIGN_IN_VIEW,
  SignInProduct,
  SignInView,
  TwoFactorMode,
} from 'common/constants/auth';
import { buildAuthSearchParams, sanitizeAuthReturnTo } from '../authSearchParams';

export interface BuildSignInUrlOptions {
  mode?: TwoFactorMode | null;
  product?: SignInProduct | null;
  returnTo?: string | null;
  source?: string | null;
  view?: SignInView | null;
}

export interface SignInRouteState {
  mode?: TwoFactorMode | null;
  product?: SignInProduct | null;
  returnTo?: string | null;
  source?: string | null;
  view: SignInView;
}

export interface ProductAccess {
  hasBothProducts: boolean;
  hasCompanyAccess: boolean;
  hasPortfolioAccess: boolean;
}

export interface PostAuthResolutionInput {
  firmListCount: number;
  isOtpVerified: boolean;
  product?: SignInProduct | null;
  requiresTerms: boolean;
  requiresTwoFactor: boolean;
  userHasTwoFactor: boolean;
  user: CustomUser;
}

// Only allow in-app return targets; this prevents auth redirects from becoming open redirects.
export const sanitizeReturnTo = sanitizeAuthReturnTo;

export const buildSignInUrl = ({
  mode,
  product,
  returnTo,
  source,
  view = SIGN_IN_VIEW.IDENTIFIER,
}: BuildSignInUrlOptions = {}) => {
  const params = buildAuthSearchParams({
    mode,
    product,
    returnTo,
    source,
    view: view && view !== SIGN_IN_VIEW.IDENTIFIER ? view : null,
  });

  const search = params.toString();
  return search ? `${signInPath}?${search}` : signInPath;
};

export const normalizeSignInRouteState = ({
  mode,
  product,
  returnTo,
  source,
  view,
}: SignInRouteState): SignInRouteState => {
  const normalizedReturnTo = sanitizeReturnTo(returnTo);

  // Each auth step owns only the query params it still needs, so stale state does not leak forward.
  switch (view) {
    case SIGN_IN_VIEW.IDENTIFIER:
    case SIGN_IN_VIEW.PASSWORD:
      return {
        mode: null,
        product: null,
        returnTo: normalizedReturnTo,
        source: source ?? null,
        view,
      };
    case SIGN_IN_VIEW.TWO_FACTOR:
      return {
        mode: mode ?? 'verify',
        product: null,
        returnTo: normalizedReturnTo,
        source: null,
        view,
      };
    case SIGN_IN_VIEW.WORKSPACE_SELECTION:
      return {
        mode: null,
        product: product ?? null,
        returnTo: normalizedReturnTo,
        source: null,
        view,
      };
    case SIGN_IN_VIEW.TERMS:
    case SIGN_IN_VIEW.PRODUCT_SELECTION:
    default:
      return {
        mode: null,
        product: null,
        returnTo: normalizedReturnTo,
        source: null,
        view,
      };
  }
};

export const toBooleanFlag = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value === 'true' || value === 'True';
  }

  return false;
};

export const getProductAccess = (user: CustomUser, firmListCount: number): ProductAccess => {
  const hasPortfolioAccess = Boolean(user.is_superuser) || Boolean(user.is_staff) || firmListCount > 0; 
  const standaloneCompaniesCount = user.standalone_companies_permissions?.length ?? 0;
  const hasCompanyAccess = Boolean(user.is_staff) || standaloneCompaniesCount > 0;

  return {
    hasBothProducts: hasPortfolioAccess && hasCompanyAccess,
    hasCompanyAccess,
    hasPortfolioAccess,
  };
};

// Read both cookie names during rollout so remembered-device survives staggered frontend deploys.
const getRememberedTwoFactorCookieNames = (userId: number) => [
  `${AUTH_OTP_REMEMBER_COOKIE_PREFIX}_${userId}`,
  `${AUTH_OTP_REMEMBER_LEGACY_COOKIE_PREFIX}_${userId}`,
];

const isLoopbackHost = (hostname: string) => hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';

const getRememberedTwoFactorCookieAttributes = (expiresAt: string) => {
  const isSecureContext = typeof window !== 'undefined'
    && (window.location.protocol === 'https:' || isLoopbackHost(window.location.hostname));

  return `SameSite=Strict; path=/; expires=${expiresAt}${isSecureContext ? '; Secure' : ''}`;
};

const getCookieValue = (name: string) => {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }

  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`));

  if (!cookie) {
    return null;
  }

  return cookie.split('=').slice(1).join('=');
};

export const hasRememberedTwoFactor = (userId?: number) => {
  if (!userId) {
    return false;
  }

  return getRememberedTwoFactorCookieNames(userId).some(cookieName => getCookieValue(cookieName) === 'true');
};

export const setRememberedTwoFactor = (userId: number, days = OTP_REMEMBER_DEVICE_DAYS) => {
  if (typeof document === 'undefined') {
    return;
  }

  const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();

  // Write both names during the migration so remembered-device state survives across staggered frontend deployments.
  getRememberedTwoFactorCookieNames(userId).forEach(cookieName => {
    document.cookie = `${cookieName}=true; ${getRememberedTwoFactorCookieAttributes(expiresAt)}`;
  });
};

export const clearRememberedTwoFactor = (userId: number) => {
  if (typeof document === 'undefined') {
    return;
  }

  getRememberedTwoFactorCookieNames(userId).forEach(cookieName => {
    document.cookie = `${cookieName}=; ${getRememberedTwoFactorCookieAttributes(new Date(0).toUTCString())}`;
  });
};

export const resolvePostAuthView = ({
  firmListCount,
  isOtpVerified,
  product,
  requiresTerms,
  requiresTwoFactor,
  userHasTwoFactor,
  user,
}: PostAuthResolutionInput): SignInView | null => {
  const mustVerifyTwoFactor = userHasTwoFactor && !isOtpVerified;
  const mustSetupTwoFactor = requiresTwoFactor && !userHasTwoFactor;

  if (requiresTerms) {
    return SIGN_IN_VIEW.TERMS;
  }

  // Blocking account requirements must be handled before product or workspace routing.
  if (mustVerifyTwoFactor || mustSetupTwoFactor) {
    return SIGN_IN_VIEW.TWO_FACTOR;
  }

  const access = getProductAccess(user, firmListCount);

  if (access.hasBothProducts && !product) {
    return SIGN_IN_VIEW.PRODUCT_SELECTION;
  }

  if (product === SIGN_IN_PRODUCT.PORTFOLIO && access.hasPortfolioAccess) {
    return SIGN_IN_VIEW.WORKSPACE_SELECTION;
  }

  if (product === SIGN_IN_PRODUCT.COMPANY && access.hasCompanyAccess) {
    return SIGN_IN_VIEW.WORKSPACE_SELECTION;
  }

  if (!product && (access.hasPortfolioAccess || access.hasCompanyAccess)) {
    return SIGN_IN_VIEW.WORKSPACE_SELECTION;
  }

  // No-access accounts still advance into workspace selection so the auth shell can render the add-firm / empty-access fallback.
  return SIGN_IN_VIEW.WORKSPACE_SELECTION;
};
